import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import BukuOutlinePage from './BukuOutlinePage'
import BukuResultPage from './BukuResultPage'
import { generateBukuOutline, countWords } from './bukuUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'
import AgentSubscriptionWrapper from '../../components/AgentSubscriptionWrapper'

// Buku Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const BukuAgentContent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [outline, setOutline] = useState([])
  const [generatedContent, setGeneratedContent] = useState({})
  const [academicSources, setAcademicSources] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()

  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOutline = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `buku_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedOutline = sessionStorage.getItem(getSessionKey('outline'))
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        
        if (persistedState && persistedOutline) {
          const state = JSON.parse(persistedState)
          const outlineData = JSON.parse(persistedOutline)
          
          console.log('📋 Loading persisted buku agent state:', state.currentState)
          setCurrentState(state.currentState)
          setOutline(outlineData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedContent) {
            const contentData = JSON.parse(persistedContent)
            setGeneratedContent(contentData)
          }
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setAcademicSources(sourcesData)
          }
          
          // Don't generate outline if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('outline'))
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('sources'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip outline generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`buku_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`buku_agent_${oldQuery}_outline`)
        sessionStorage.removeItem(`buku_agent_${oldQuery}_content`)
        sessionStorage.removeItem(`buku_agent_${oldQuery}_sources`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate initial outline only if needed
    if (query && !hasInitialized && !isGeneratingOutline.current && !hasEverGenerated.current) {
      generateInitialOutline()
    }
  }, [query]) // Only depend on query, not llmService

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && outline.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('outline'), JSON.stringify(outline))
        
        if (Object.keys(generatedContent).length > 0) {
          sessionStorage.setItem(getSessionKey('content'), JSON.stringify(generatedContent))
        }
        
        if (academicSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(academicSources))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, outline, generatedContent, academicSources, hasInitialized, query])

  const generateInitialOutline = async () => {
    if (isGeneratingOutline.current || hasEverGenerated.current) {
      console.log('⚠️ Outline generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingOutline.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      console.log('🔄 Generating initial outline for:', query)
      
      const result = await generateBukuOutline(query, llmService)
      setOutline(result.outline)
      setAcademicSources(result.academicSources)
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial outline generated successfully')
    } catch (error) {
      console.error('❌ Error generating initial outline:', error)
      setError('Gagal membuat outline awal')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isGeneratingOutline.current = false
    }
  }

  const handleStartGeneration = async (finalOutline) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.BUKU)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setOutline(finalOutline)
    setProgress({ current: 0, total: finalOutline.length })
    setIsLoading(true)
    setError(null)
    // Jangan reset academicSources karena sudah didapat dari outline generation
    
    try {
      const content = {}
      
      // Generate content for each section sequentially
      for (let i = 0; i < finalOutline.length; i++) {
        const section = finalOutline[i]
        
        // Update progress
        setProgress(prev => ({ ...prev, current: i + 1 }))
        
        console.log(`🔄 Memproses bagian ${i + 1}/${finalOutline.length}: ${section.title}`)
        
        // Generate content for this section with previous content for continuity
        // Gunakan academicSources yang sudah ada, tidak perlu fetch lagi
        const sectionContent = await generateSectionContent(section, query, llmService, content, academicSources)
        content[section.id] = sectionContent
        
        // Update generated content
        setGeneratedContent(prevContent => ({ ...prevContent, [section.id]: sectionContent }))
        
        console.log(`✅ Selesai memproses: ${section.title}`)
        
        // Small delay to prevent overwhelming the API
        if (i < finalOutline.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
      
      // Add bibliography section to outline and content
      const bibliographySection = {
        id: 'daftar_pustaka_final',
        title: 'DAFTAR PUSTAKA',
        description: 'Daftar pustaka lengkap dari semua sumber yang digunakan',
        minWords: 0,
        isEditable: false
      }
      
      const bibliographyContent = academicSources.map((source, index) => {
        const year = new Date().getFullYear()
        return `${index + 1}. ${source.title} (${year}). Retrieved from ${source.url}`
      }).join('\n\n')
      
      setOutline(prev => [...prev, bibliographySection])
      setGeneratedContent(prev => ({ ...prev, [bibliographySection.id]: bibliographyContent }))
      
      console.log('🎉 Semua bagian buku selesai diproses')
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful completion
      try {
        const creditResult = await consumeCredits(AGENT_TYPES.BUKU)
        if (creditResult.success) {
          console.log('✅ Kredit berhasil dikonsumsi untuk buku agent')
        } else {
          console.warn('⚠️ Gagal mengkonsumsi kredit:', creditResult.error)
        }
      } catch (creditError) {
        console.error('❌ Error saat mengkonsumsi kredit:', creditError)
      }
      
      // Save to agent history after successful completion
      try {
        const saveResult = await AgentHistoryIntegration.saveBukuResult({
          query: query,
          outline: finalOutline,
          generatedContent: content,
          academicSources: academicSources,
          progress: { current: finalOutline.length, total: finalOutline.length }
        })
        
        if (saveResult.success) {
          console.log('✅ Buku berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan buku ke history:', saveResult.error)
        }
      } catch (saveError) {
        console.error('❌ Error saat menyimpan ke history:', saveError)
        // Don't throw error here, just log it - the buku generation was successful
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      setError(err.message)
      setCurrentState(AGENT_STATES.OUTLINE)
    } finally {
      setIsLoading(false)
    }
  }

  const generateSectionContent = async (section, originalQuery, llmService, previousContent = {}, existingAcademicSources = []) => {
    // Gunakan sumber akademik yang sudah ada dari outline generation
    let sourcesText = ''
    
    if (existingAcademicSources && existingAcademicSources.length > 0) {
      // Filter sumber yang relevan dengan section ini
      const relevantSources = existingAcademicSources.filter(source => {
        const sectionKeywords = section.title.toLowerCase().split(' ')
        const sourceText = `${source.title} ${source.snippet}`.toLowerCase()
        return sectionKeywords.some(keyword => 
          keyword.length > 3 && sourceText.includes(keyword)
        )
      })
      
      // Gunakan lebih banyak sumber untuk buku (10-15 sumber per bagian)
      const sourcesToUse = relevantSources.length > 0 ? 
        relevantSources.slice(0, Math.min(15, relevantSources.length)) : 
        existingAcademicSources.slice(0, Math.min(15, existingAcademicSources.length))
      
      if (sourcesToUse.length > 0) {
        sourcesText = `SUMBER AKADEMIK WAJIB UNTUK REFERENSI (${sourcesToUse.length} sumber global berkualitas tinggi):\n\n`
        sourcesText += sourcesToUse.map((source, index) => 
          `[${index + 1}] ${source.title}\n   Penulis: ${source.authors?.join(', ') || 'N/A'}\n   Tahun: ${source.year || 'N/A'}\n   Jurnal: ${source.journal || 'N/A'}\n   Abstrak: ${source.snippet}\n   URL: ${source.url}\n`
        ).join('\n')
        
        sourcesText += `\n\nINSTRUKSI PENGGUNAAN SUMBER:\n- WAJIB merujuk dan mengutip minimal 5-8 sumber dari daftar di atas dalam bagian ini\n- Gunakan format sitasi yang sesuai dengan jenis buku\n- Integrasikan temuan dan data dari sumber-sumber tersebut ke dalam pembahasan\n- Pastikan setiap klaim didukung oleh referensi akademik yang kredibel\n- Prioritaskan sumber dari jurnal internasional dan institusi ternama\n`
        
        console.log(`✅ Menggunakan ${sourcesToUse.length} sumber akademik global berkualitas tinggi untuk: ${section.title}`)
      }
    } else {
      console.log('⚠️ Tidak ada sumber akademik tersedia, menggunakan pengetahuan umum')
    }
    
    // Buat konteks dari SEMUA bagian sebelumnya untuk kontinuitas penuh
    let previousContext = '';
    const previousSections = Object.keys(previousContent);
    if (previousSections.length > 0) {
      previousContext = `\n\nKONTEKS LENGKAP DARI SEMUA BAGIAN SEBELUMNYA (untuk kontinuitas dan kohesi):\n`;
      previousSections.forEach(sectionId => {
        const content = previousContent[sectionId];
        if (content) {
          // Sertakan SELURUH konten dari bagian sebelumnya
          previousContext += `\n=== BAGIAN: ${sectionId.toUpperCase()} ===\n${content}\n\n`;
        }
      });
      previousContext += `\n=== INSTRUKSI KONTINUITAS ===\nPASTIKAN bagian "${section.title}" yang akan Anda tulis:\n- TERHUBUNG secara logis dengan semua bagian sebelumnya\n- MENGGUNAKAN transisi yang tepat dari bagian terakhir\n- MERUJUK kembali konsep, teori, atau temuan yang telah dibahas sebelumnya\n- MEMBANGUN argumen yang berkelanjutan dari fondasi yang sudah diletakkan\n- MENGHINDARI pengulangan yang tidak perlu\n- MENJAGA alur logis dan kohesi keseluruhan buku\n`;
    }
    
    // Buat prompt untuk menghasilkan KONTEN LENGKAP dalam format Markdown
    const basePrompt = `Tulis KONTEN LENGKAP untuk bagian "${section.title}" dalam buku dengan topik "${originalQuery}". JANGAN SERTAKAN KALIMAT BASA-BASI DI AWAL OUTPUT.`;
    
    const guidelines = `\nPedoman WAJIB:
    - LANGSUNG tulis konten lengkap dan komprehensif tanpa kalimat pembuka atau basa-basi
    - MULAI LANGSUNG dengan heading utama bagian (contoh: ## BAB 1: PENGANTAR)
    - TARGET KATA MINIMAL: ${section.minWords} kata - INI ADALAH PERSYARATAN MUTLAK!
    - JIKA TARGET KATA TIDAK TERCAPAI, KONTEN AKAN DITOLAK!
    - Untuk mencapai target kata, WAJIB:
      * Tulis setiap sub-bagian dengan sangat detail dan komprehensif
      * Berikan contoh-contoh konkret dan studi kasus
      * Sertakan analisis mendalam untuk setiap poin
      * Tambahkan pembahasan teoritis yang ekstensif
      * Gunakan paragraf yang panjang dan substantif (minimal 200-300 kata per paragraf)
      * Berikan penjelasan step-by-step untuk setiap konsep
      * Sertakan cerita, anekdot, dan ilustrasi yang relevan
      * Tambahkan tips praktis dan actionable insights
    - Gunakan format Markdown dengan heading, subheading, dan formatting yang tepat
    - Untuk list dan nested list, gunakan format yang benar:
      * Gunakan - untuk list utama
      * Gunakan spasi + - untuk nested list (contoh: "  - sub item")
      * Pastikan konsistensi indentasi
    - WAJIB: Semua tabel HARUS dibuat dalam format Markdown yang benar dengan | dan header yang jelas
    - Contoh format tabel yang benar:
      | Header 1 | Header 2 | Header 3 |
      |----------|----------|----------|
      | Data 1   | Data 2   | Data 3   |
    - Sertakan sitasi dalam format yang sesuai dengan jenis buku
    - Tulis dengan gaya yang sesuai dengan target pembaca buku
    - Berikan analisis mendalam dan pembahasan yang substantif
    - Gunakan paragraf yang koheren dan terstruktur dengan baik
    - PENTING: Pastikan bagian ini terhubung logis dengan bagian sebelumnya
    - Gunakan transisi yang tepat dan rujuk konsep yang relevan dari bagian sebelumnya
    - WAJIB: Jika ada data, statistik, perbandingan, atau informasi yang dapat disajikan dalam bentuk tabel, maka HARUS dibuat dalam format tabel Markdown
    - Perluas setiap sub-bagian dengan detail yang mendalam dan contoh konkret
    - Tambahkan analisis kritis dan evaluasi yang komprehensif
    - Sertakan elemen storytelling yang menarik untuk membuat buku lebih engaging
    - Berikan insights praktis yang dapat diterapkan pembaca
    - KHUSUS UNTUK BAB INTI: Wajib mencapai target kata dengan:
      * Pembahasan yang sangat detail dan komprehensif
      * Contoh-contoh nyata dan studi kasus
      * Tips dan strategi praktis
      * Analisis mendalam dari berbagai perspektif
      * Diskusi pro dan kontra dari setiap konsep
      * Implementasi praktis dan langkah-langkah konkret
    ${section.description ? `- Deskripsi bagian: ${section.description}` : ''}
    ${section.subsections && section.subsections.length > 0 ? `- Sub-bagian yang harus dicakup: ${section.subsections.join(', ')}` : ''}`;
    
    let prompt;
    if (sourcesText) {
      prompt = `${basePrompt}${previousContext}

Sumber Akademik Referensi:
${sourcesText}
${guidelines}

Gunakan sumber-sumber akademik di atas sebagai referensi utama. Sitasi harus menggunakan format yang sesuai dengan jenis buku.

Tulis konten lengkap dalam format Markdown:`;
    } else {
      prompt = `${basePrompt}${previousContext}${guidelines}

Tulis konten lengkap dalam format Markdown dengan referensi yang relevan:`;
    }

    try {
      console.log(`🤖 Menggenerate konten untuk: ${section.title}`);
      
      // Retry mechanism dengan timeout yang lebih panjang
      let response;
      let lastError;
      const maxRetries = 3;
      const timeoutDuration = 180000; // 3 menit timeout untuk konten buku yang panjang
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`📝 Percobaan ${attempt}/${maxRetries} untuk ${section.title}`);
          
          const llmTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout LLM service')), timeoutDuration)
          );
          
          // Use GENIUS mode for comprehensive book writing
          response = await Promise.race([
            llmService.sendMessage(
              [{ role: 'user', content: prompt }],
              { 
                mode: MODEL_MODES.GENIUS,
                searchType: 'academic',
                stream: false,
                consumeCredits: false // Don't consume credits for content generation
              }
            ),
            llmTimeoutPromise
          ]);
          
          // Jika berhasil, keluar dari loop retry
          break;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Percobaan ${attempt} gagal:`, error.message);
          
          if (attempt < maxRetries) {
            const delay = attempt * 3000; // Progressive delay: 3s, 6s
            console.log(`⏳ Menunggu ${delay/1000}s sebelum percobaan berikutnya...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      if (!response) {
        throw lastError || new Error('Gagal setelah beberapa percobaan');
      }
      
      // Validasi jumlah kata
      const wordCount = countWords(response);
      const targetWords = section.minWords;
      
      console.log(`📊 Jumlah kata yang dihasilkan: ${wordCount.toLocaleString()} (target: ${targetWords.toLocaleString()})`);
      
      // Jika jumlah kata kurang dari 80% target, coba lagi dengan prompt yang lebih tegas
      if (wordCount < targetWords * 0.8 && targetWords > 0) {
        console.warn(`⚠️ Jumlah kata kurang dari target (${wordCount} < ${targetWords}). Mencoba lagi dengan prompt yang lebih tegas...`);
        
        const enhancedPrompt = `${prompt}

PERINGATAN KRITIS: Konten sebelumnya hanya ${wordCount} kata, JAUH di bawah target ${targetWords} kata!

WAJIB MENGHASILKAN MINIMAL ${targetWords} KATA dengan cara:
1. Perluas SETIAP paragraf menjadi minimal 300-400 kata
2. Tambahkan sub-bagian detail untuk setiap poin utama
3. Berikan contoh konkret dan studi kasus untuk setiap konsep
4. Sertakan analisis mendalam dan pembahasan teoritis
5. Tambahkan tabel perbandingan dan data pendukung
6. Berikan penjelasan step-by-step yang sangat detail
7. Diskusikan implikasi praktis dan teoritis secara ekstensif
8. Sertakan cerita dan anekdot yang relevan
9. Tambahkan tips dan strategi praktis yang actionable
10. Berikan multiple perspectives dan analisis komprehensif

JANGAN BERHENTI SAMPAI MENCAPAI ${targetWords} KATA!`;
        
        try {
          const enhancedResponse = await llmService.sendMessage(
            [{ role: 'user', content: enhancedPrompt }],
            { 
              mode: MODEL_MODES.GENIUS,
              searchType: 'academic',
              stream: false,
              consumeCredits: false // Don't consume credits for enhanced content generation
            }
          );
          
          const enhancedWordCount = countWords(enhancedResponse);
          console.log(`📊 Jumlah kata setelah enhancement: ${enhancedWordCount.toLocaleString()}`);
          
          if (enhancedWordCount > wordCount) {
            response = enhancedResponse;
            console.log(`✅ Konten berhasil diperbaiki untuk: ${section.title} (${enhancedWordCount} kata)`);
          } else {
            console.log(`⚠️ Enhancement tidak berhasil, menggunakan konten asli`);
          }
        } catch (enhanceError) {
          console.warn(`⚠️ Error saat enhancement:`, enhanceError.message);
          console.log(`📝 Menggunakan konten asli`);
        }
      } else {
        console.log(`✅ Konten berhasil dibuat untuk: ${section.title} (${wordCount} kata)`);
      }
      
      // Return hanya content karena sources sudah ada
      return response
    } catch (error) {
      console.error(`❌ Error menggenerate konten untuk ${section.title}:`, error.message);
      throw new Error(`Gagal menggenerate konten untuk ${section.title}: ${error.message}`);
    }
  }

  const handleBackToOutline = () => {
    // Clear generated content from session storage but keep outline
    try {
      sessionStorage.removeItem(getSessionKey('content'))
    } catch (error) {
      console.warn('⚠️ Failed to clear session storage:', error)
    }
    
    setCurrentState(AGENT_STATES.OUTLINE)
    setGeneratedContent({})
    setProgress({ current: 0, total: 0 })
    setError(null)
    // Jangan reset academicSources dan hasInitialized karena outline sudah ada
  }

  const handleBackToSearch = () => {
    // Clear session storage when going back to search
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('outline'))
      sessionStorage.removeItem(getSessionKey('content'))
      sessionStorage.removeItem(getSessionKey('sources'))
    } catch (error) {
      console.warn('⚠️ Failed to clear session storage:', error)
    }
    
    // Reset state and go back to search
    setCurrentState(AGENT_STATES.OUTLINE)
    setOutline([])
    setGeneratedContent({})
    setAcademicSources([])
    setProgress({ current: 0, total: 0 })
    setError(null)
    setHasInitialized(false)
    hasEverGenerated.current = false
    isGeneratingOutline.current = false
    onBack()
  }

  const renderCurrentState = () => {
    // Show loading state while generating initial outline
    if (currentState === AGENT_STATES.OUTLINE && isLoading && outline.length === 0) {
      return (
        <div className="min-h-screen bg-bg-primary flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-text-secondary">Membuat outline buku...</p>
          </div>
        </div>
      )
    }
    
    // Show error state if outline generation failed
    if (currentState === AGENT_STATES.OUTLINE && error && outline.length === 0) {
      return (
        <div className="min-h-screen bg-bg-primary flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">Gagal Membuat Outline</h3>
            <p className="text-text-secondary mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null)
                setIsLoading(false)
                setHasInitialized(false) // Reset untuk memicu ulang generateInitialOutline
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Coba Lagi
            </button>
          </div>
        </div>
      )
    }
    
    switch (currentState) {
      case AGENT_STATES.OUTLINE:
        return (
          <BukuOutlinePage
          query={query}
          outline={outline}
          onOutlineChange={setOutline}
          onStartGeneration={handleStartGeneration}
          onBack={onBack}
          llmService={llmService}
        />
        )
      
      case AGENT_STATES.GENERATING:
      case AGENT_STATES.COMPLETED:
        return (
          <BukuResultPage
            query={query}
            outline={outline}
            generatedContent={generatedContent}
            academicSources={academicSources}
            progress={progress}
            isLoading={isLoading}
            error={error}
            isCompleted={currentState === AGENT_STATES.COMPLETED}
            onBack={handleBackToOutline}
            onBackToSearch={handleBackToSearch}
          />
        )
      
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {renderCurrentState()}
      <CreditWarningModal 
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        message={warningModal.message}
      />
    </div>
  )
}

BukuAgentContent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

const BukuAgent = (props) => {
  return (
    <AgentSubscriptionWrapper actionType="buku_agent" {...props}>
      <BukuAgentContent {...props} />
    </AgentSubscriptionWrapper>
  )
}

export default BukuAgent